/**
 * Scroll the first invalid form control within `root` into view.
 *
 * Prefers `[aria-invalid="true"]` (which every Iris form control sets from the same `invalid &&
 * touched` contract that renders its error text), then falls back to Angular's own `.ng-invalid`
 * marker so controls that aren't `iris-form-field`s — e.g. `sonder-image-upload` — are still found.
 * Call `markAllAsTouched()` on the form before invoking so the invalid state is reflected in the DOM.
 *
 * We scroll but deliberately do NOT move focus: the Iris error text is `aria-live="assertive"`, so
 * screen readers announce it without a focus change, and forcing focus on mobile would summon the
 * soft keyboard unexpectedly. This matches the scroll-only error handling used elsewhere in the app
 * (e.g. bv-error, insurance-add banners). Smooth scrolling is skipped under reduced-motion, and the
 * call is deferred to the next macrotask so the error markup has rendered before we query.
 */
export function scrollToFirstInvalidControl(root: HTMLElement | null | undefined): void {
  if (!root) {
    return;
  }

  setTimeout(() => {
    // `.ng-invalid` matches nested controls too, so exclude ancestor form/group wrappers that are
    // only invalid because a descendant is — we want the actual offending control.
    const ngInvalid = Array.from(root.querySelectorAll<HTMLElement>('.ng-invalid'))
      .find((el) => !el.querySelector('.ng-invalid'));
    const invalid = root.querySelector<HTMLElement>('[aria-invalid="true"]') ?? ngInvalid;
    if (!invalid) {
      return;
    }

    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    (invalid.closest('iris-form-field') ?? invalid).scrollIntoView({ behavior, block: 'center' });
  }, 0);
}
